// ScanVizCard.tsx — the Viewer's "Scan Visualization" pane under the model:
// the keyframe the phone actually captured beside the splats rendered from
// that same camera (refined pose + intrinsics once bundle-adjusted, else the
// SLAM ones), so mismatches between the model and the real feed are easy to
// spot. A slider, step buttons and Play walk the camera path; the model card
// highlights the same camera. Three modes share one DOM (so the WebGL canvas
// is never remounted): side by side, overlay with an opacity slider, and a
// difference blend. The render is an ideal pinhole image; lens distortion in
// the JPEG is not undone, so expect small misalignment near the corners.
// Once refined, the camera frame's border shows the keyframe's loss weight
// (white at the floor → green at full) with the exact numbers on hover; a
// keyframe whose motion blur the refinement modelled gets a dashed border
// and a "blur" note in its caption, with the exposure path on hover.
//
// Image Degradation (the header switch) checks the refine's lens model: the
// splats are rendered blurred the way the model says the camera blurred
// this keyframe — its learned defocus (focus plane ρ, aperture α) and motion
// (exposure path φ, δ) per splat, exactly as the trainer renders a training
// view (splat/lens.ts) — and the photo is undistorted into the same pinhole
// image the trainer compares against, so the two can be judged like for
// like. A readout under the frames gives the keyframe's exposure, smear,
// focus distance against scene depth and defocus σ, each explained on
// hover/tap. Flip mode stacks photo and render and swaps them on a tap, the
// easiest way to see whether the blur matches on a phone. The switch is
// disabled, with the reason on hover, when the refinement has no lens model.
import { useCallback, useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { BsPauseFill, BsPlayFill, BsSkipBackwardFill, BsSkipForwardFill } from 'react-icons/bs';
import { urls } from '../api';
import type { Scan } from '../api';
import { intrinsicsProjection, poseToGLView, scaleIntrinsics } from '../splat/camera';
import type { SplatCloud } from '../splat/format';
import { hasDistortion, hasLens, lensFromBlur, undistortRGBA } from '../splat/lens';
import type { Lens } from '../splat/lens';
import { SplatRenderer } from '../splat/renderer';
import { blurTitle, cameraTitle, lensReadout, weightColor } from './trajectory';
import type { PathCamera } from './trajectory';

type Mode = 'side' | 'flip' | 'overlay' | 'diff';
const MODES: { value: Mode; label: string }[] = [
  { value: 'side', label: 'Side by side' },
  { value: 'flip', label: 'Flip' },
  { value: 'overlay', label: 'Overlay' },
  { value: 'diff', label: 'Difference' },
];
const PLAY_STEP_MS = 250;
const NEAR_M = 0.01;
const FAR_M = 100;
// The undistorted photo's long side (px): enough to judge a few-pixel blur
// on any screen, small enough to remap in a few tens of ms.
const UNDISTORT_MAX_PX = 1280;

// useUndistortedPhoto returns an object URL of the keyframe photo remapped
// into the pinhole image of intrinsics k (core/camera's undistortion, the
// one the trainer applies before it blurs), or null while it is not ready,
// not wanted, not needed (no distortion) or not possible (no 2D canvas).
// A newer request supersedes an older one still loading.
function useUndistortedPhoto(src: string | null, k: PathCamera['intrinsics'] | null, enabled: boolean): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    setUrl(null);
    if (!enabled || !src || !k || !hasDistortion(k)) return;
    let cancelled = false;
    let made: string | null = null;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      const t0 = performance.now();
      const scale = Math.min(1, UNDISTORT_MAX_PX / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.max(1, Math.round(img.naturalWidth * scale)), h = Math.max(1, Math.round(img.naturalHeight * scale));
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      if (!ctx) {
        console.warn('[openscan/scanviz] no 2D canvas: showing the raw (distorted) photo');
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      const data = ctx.getImageData(0, 0, w, h);
      data.data.set(undistortRGBA(data.data, w, h, { ...scaleIntrinsics(k, w, h), k1: k.k1, k2: k.k2, p1: k.p1, p2: k.p2 }));
      ctx.putImageData(data, 0, 0);
      c.toBlob((b) => {
        if (cancelled || !b) return;
        made = URL.createObjectURL(b);
        setUrl(made);
        console.debug('[openscan/scanviz] undistorted keyframe', { src, w, h, ms: Math.round(performance.now() - t0) });
      }, 'image/png');
    };
    img.onerror = () => console.warn('[openscan/scanviz] keyframe photo failed to load for undistortion', src);
    img.src = src;
    return () => {
      cancelled = true;
      if (made) URL.revokeObjectURL(made);
    };
  }, [src, k, enabled]);
  return url;
}

const NO_LENS_REASON =
  "This scan's refinement did not run the lens model, so there is no per-keyframe defocus or exposure to render. Re-refine to learn one.";

export interface ScanVizCardProps {
  scan: Scan;
  cloud: SplatCloud | null;
  path: PathCamera[];
  active: number; // index into path
  onActive: (i: number) => void;
  rmsPx?: number;
  rollingShutter?: number; // the refinement's readout/exposure ratio (0 = not modelled)
}

export function ScanVizCard({ scan, cloud, path, active, onActive, rmsPx, rollingShutter }: ScanVizCardProps) {
  const [mode, setMode] = useState<Mode>('side');
  const [degrade, setDegrade] = useState(false);
  const [flipRender, setFlipRender] = useState(false); // Flip mode: render on top
  const [opacity, setOpacity] = useState(0.5);
  const [playing, setPlaying] = useState(false);
  const [glError, setGlError] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<SplatRenderer | null>(null);
  const camRef = useRef<PathCamera | null>(null);
  const rafRef = useRef(0);

  const cam = path[active] ?? null;
  camRef.current = cam;
  const lensAvailable = path.some((c) => hasLens(c.blur));
  const degradeOn = degrade && lensAvailable;
  const lens: Lens | null = degradeOn ? lensFromBlur(cam?.blur) : null;
  const lensRef = useRef<Lens | null>(null);
  lensRef.current = lens;
  const rawPhoto = cam ? urls.keyframe(scan.id, cam.index) : null;
  const undistorted = useUndistortedPhoto(rawPhoto, cam?.intrinsics ?? null, degradeOn);
  const distorted = !!cam && hasDistortion(cam.intrinsics);

  // draw renders the splats from the active camera on the next frame.
  const draw = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      const r = rendererRef.current, c = canvasRef.current, k = camRef.current;
      if (!r || !c || !k) return;
      const ki = scaleIntrinsics(k.intrinsics, c.width, c.height);
      const lens = lensRef.current;
      r.render({ view: poseToGLView(k.pose), proj: intrinsicsProjection(ki, NEAR_M, FAR_M), fx: ki.fx, fy: ki.fy, lens });
      console.debug('[openscan/scanviz] rendered keyframe', { index: k.index, refined: k.refined, w: c.width, h: c.height, splats: r.renderedCount, lens });
    });
  }, []);

  // One renderer for the card's lifetime, resized with its frame.
  useEffect(() => {
    const c = canvasRef.current, frame = frameRef.current;
    if (!c || !frame) return;
    let r: SplatRenderer;
    try {
      r = new SplatRenderer(c);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[openscan/scanviz]', msg);
      setGlError(msg);
      return;
    }
    rendererRef.current = r;
    const resize = (): void => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(c.clientWidth * dpr)), h = Math.max(1, Math.round(c.clientHeight * dpr));
      if (c.width !== w || c.height !== h) {
        c.width = w;
        c.height = h;
        draw();
      }
    };
    const ro = new ResizeObserver(resize);
    ro.observe(frame);
    resize();
    return () => {
      ro.disconnect();
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      r.dispose();
      rendererRef.current = null;
    };
  }, [draw]);

  useEffect(() => {
    const r = rendererRef.current;
    if (!r) return;
    if (cloud) r.setCloud(cloud);
    draw();
  }, [cloud, draw]);

  useEffect(draw, [cam, lens, draw]);

  // Playback steps along the path and stops at its end.
  useEffect(() => {
    if (!playing) return;
    if (active >= path.length - 1) {
      setPlaying(false);
      return;
    }
    const t = window.setTimeout(() => onActive(active + 1), PLAY_STEP_MS);
    return () => window.clearTimeout(t);
  }, [playing, active, path.length, onActive]);

  // Warm the cache for the next few photos so playback doesn't flash.
  useEffect(() => {
    for (let i = active + 1; i <= active + 3 && i < path.length; i++) {
      const img = new Image();
      img.src = urls.keyframe(scan.id, path[i].index);
    }
  }, [active, path, scan.id]);

  const n = path.length;
  const aspect = cam && cam.intrinsics.height > 0 ? cam.intrinsics.width / cam.intrinsics.height : 4 / 3;
  const t0 = n > 0 ? path[0].timestampMs : 0;
  const play = () => {
    if (active >= n - 1) onActive(0);
    setPlaying(true);
  };

  return (
    <Card>
      <Card.Header className="d-flex flex-wrap align-items-center gap-2">
        <span className="me-auto">Scan Visualization</span>
        <span className="small text-body-secondary">
          {n === 0 ? '' : cam?.refined ? `refined cameras${rmsPx ? ` · ${rmsPx.toFixed(2)} px RMS` : ''}` : 'phone SLAM cameras'}
        </span>
        <OverlayTrigger
          placement="bottom"
          overlay={
            <Tooltip id="scanviz-degrade-tip">
              {lensAvailable
                ? "Render the splats blurred the way the lens model says the camera blurred this keyframe (its defocus and motion over the exposure), and undistort the photo, to check the model against the real frame."
                : NO_LENS_REASON}
            </Tooltip>
          }
        >
          <span className="d-inline-block" tabIndex={lensAvailable ? undefined : 0}>
            <Form.Check
              type="switch"
              id="scanviz-degrade"
              label="Image Degradation"
              checked={degradeOn}
              disabled={!lensAvailable}
              onChange={(e) => setDegrade(e.target.checked)}
            />
          </span>
        </OverlayTrigger>
        <ButtonGroup size="sm" aria-label="Comparison mode">
          {MODES.map((m) => (
            <Button key={m.value} variant={mode === m.value ? 'primary' : 'outline-primary'} onClick={() => setMode(m.value)}>
              {m.label}
            </Button>
          ))}
        </ButtonGroup>
      </Card.Header>
      <Card.Body>
        <div
          className={`scanviz scanviz-${mode}${mode === 'flip' && flipRender ? ' scanviz-flip-render' : ''}`}
          onClick={mode === 'flip' ? () => setFlipRender((f) => !f) : undefined}
          style={{ ['--scanviz-opacity' as string]: opacity, ['--scanviz-ar' as string]: aspect }}>
          <figure
            className={`scanviz-frame scanviz-photo${cam?.weight ? ' scanviz-weighted' : ''}${cam?.blur ? ' scanviz-blurred' : ''}`}
            style={cam?.weight ? { ['--scanviz-weight-color' as string]: weightColor(cam.weight.weight) } : undefined}
            title={cam ? cameraTitle(cam, rollingShutter) : undefined}
            data-weight={cam?.weight?.weight}
            data-blur-px={cam?.blur?.smear_px}
          >
            {cam && <img src={undistorted ?? rawPhoto ?? undefined} alt={`Keyframe ${cam.index} as captured`} draggable={false} />}
            <figcaption>
              Camera{undistorted ? ' (undistorted)' : ''}{cam?.blur ? ` · blur ${cam.blur.smear_px.toFixed(1)} px` : ''}
              {cam?.blur && <span className="visually-hidden">{`: ${blurTitle(cam.blur, rollingShutter)}`}</span>}
            </figcaption>
          </figure>
          <figure ref={frameRef} className="scanviz-frame scanviz-render">
            <canvas ref={canvasRef} />
            <figcaption>{lens ? 'Splats, degraded' : 'Splats'}</figcaption>
            {(glError || !cloud || cloud.count === 0) && <div className="splat-view-empty">{glError || 'No splats yet'}</div>}
          </figure>
        </div>
        {mode === 'flip' && n > 0 && (
          <div className="small text-body-secondary mt-2">
            Showing the {flipRender ? (lens ? 'degraded render' : 'render') : 'photo'} — tap the image to flip.
          </div>
        )}
        {degradeOn && cam && <LensReadout cam={cam} lens={lens} undistorted={!!undistorted} distorted={distorted} />}
        {n === 0 && <div className="small text-body-secondary mt-2">No keyframes yet — the camera path appears as the phone uploads them.</div>}
      </Card.Body>
      <Card.Footer className="d-flex flex-wrap align-items-center gap-2">
        <ButtonGroup size="sm">
          <Button variant="outline-secondary" disabled={active <= 0} onClick={() => onActive(active - 1)} aria-label="Previous keyframe">
            <BsSkipBackwardFill />
          </Button>
          <Button variant="outline-secondary" disabled={n < 2} onClick={playing ? () => setPlaying(false) : play} aria-label={playing ? 'Pause' : 'Play'}>
            {playing ? <BsPauseFill /> : <BsPlayFill />}
          </Button>
          <Button variant="outline-secondary" disabled={active >= n - 1} onClick={() => onActive(active + 1)} aria-label="Next keyframe">
            <BsSkipForwardFill />
          </Button>
        </ButtonGroup>
        <Form.Range
          className="flex-grow-1"
          style={{ minWidth: '8rem' }}
          min={0}
          max={Math.max(0, n - 1)}
          step={1}
          value={Math.min(active, Math.max(0, n - 1))}
          disabled={n < 2}
          onChange={(e) => {
            setPlaying(false);
            onActive(Number(e.target.value));
          }}
          aria-label="Keyframe along the camera path"
        />
        <span className="small measure-value">
          {cam ? `#${cam.index} · ${active + 1}/${n} · ${((cam.timestampMs - t0) / 1000).toFixed(1)} s` : '—'}
        </span>
        {mode === 'overlay' && (
          <Form.Range
            style={{ maxWidth: '8rem' }}
            min={0}
            max={1}
            step={0.05}
            value={opacity}
            onChange={(e) => setOpacity(Number(e.target.value))}
            aria-label="Splat overlay opacity"
          />
        )}
      </Card.Footer>
    </Card>
  );
}

// LensReadout is the Image Degradation readout under the frames: the
// keyframe's learned exposure, motion smear, focus against scene depth and
// defocus σ, each with its explanation on hover or tap, plus how the photo
// was prepared for the comparison.
function LensReadout({ cam, lens, undistorted, distorted }: { cam: PathCamera; lens: Lens | null; undistorted: boolean; distorted: boolean }) {
  const items = cam.blur ? lensReadout(cam.blur) : [];
  const photoNote = !distorted
    ? 'The camera has no lens distortion, so the photo is compared as captured.'
    : undistorted
      ? 'The photo is undistorted into the same pinhole image the trainer blurs and compares against.'
      : 'The photo is shown as captured (lens-distorted) while it is undistorted; the render is pinhole, so judge blur near the centre.';
  return (
    <div className="scanviz-readout mt-2">
      {!lens || items.length === 0 ? (
        <div className="small text-body-secondary">Keyframe #{cam.index} has no learned lens; the splats are shown sharp.</div>
      ) : (
        <dl className="scanviz-readout-grid small mb-1">
          {items.map((it) => (
            <OverlayTrigger key={it.key} placement="top" trigger={['hover', 'focus', 'click']} rootClose overlay={<Tooltip id={`scanviz-lens-${it.key}`}>{it.help}</Tooltip>}>
              <div className="scanviz-readout-item" tabIndex={0} data-lens={it.key}>
                <dt className="text-body-secondary fw-normal">{it.label}</dt>
                <dd className="scanviz-readout-value mb-0">{it.value}</dd>
              </div>
            </OverlayTrigger>
          ))}
        </dl>
      )}
      <div className="small text-body-secondary">{photoNote}</div>
    </div>
  );
}
